<template>
  <div class="main-wrapper">
    <sidebar-menu
      width="300px"
      widthCollapsed="50px"
      :menu="menu"
      :relative="true"
      :collapsed="collapsed"
      @update:collapsed="updateCollapsed($event)"
      class="side-menu"
    >
      <template #toggle-icon>
        <v-tooltip location="end" text="開く / 閉じる">
          <template v-slot:activator="{ props }">
            <v-icon v-bind="props" icon="$mdiFormatAlignJustify"></v-icon>
          </template>
        </v-tooltip>
      </template>
    </sidebar-menu>
    <div class="main-container">
      <router-view />
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import type {
  SidebarComponentItem,
  SidebarHeaderItem,
  SidebarItem,
} from "vue-sidebar-menu";
import { useSideMenu } from "@/composable/side-menu";
import type PermissionService from "@/services/PermissionService";
import type {
  ContractAppliances,
  ContractInfo,
  ContractSummary,
} from "@/apis/ContractApi";
import { mapState } from "vuex";

export default defineComponent({
  name: "NWSettingMain",
  setup() {
    const { collapsed, updateCollapsed } = useSideMenu();
    return { collapsed, updateCollapsed };
  },
  computed: {
    ...mapState("user", {
      contractInfo: "contractInfo",
      contractSummary: "contractSummary",
      contractAppliances: "contractAppliances",
    }),
    menu(): Array<SidebarItem | SidebarComponentItem | SidebarHeaderItem> {
      return getNWSettingMenus(
        this.contractInfo.contractList,
        this.contractSummary,
        this.contractAppliances.applianceInfoList,
        this.$service.permission
      );
    },
  },
});

/** NW設定メニューを取得 */
export function getNWSettingMenus(
  contractList: ContractInfo["contractList"],
  contractSummary: ContractSummary,
  applianceInfoList: ContractAppliances["applianceInfoList"],
  permission: PermissionService
): Array<SidebarItem | SidebarComponentItem | SidebarHeaderItem> {
  const hasMultiCloudGW = contractList.some(
    (e) => e.accessType === "MULTI_CLOUD_GW"
  );
  const hasIPsec = contractList.some((e) => e.accessType === "IPSEC");
  const hasType1orIntranet = applianceInfoList.some(
    (e) => e.internetType === "TYPE1" || e.internetType === "INTRANET"
  );
  const isViewDelegate = permission.isViewDelegate();
  const hasCpa5Gsa = contractList.some((e) => e.accessType === "CPA_5GSA");
  return [
    {
      header: "アクセス回線",
      hiddenOnCollapse: true,
    },
    {
      href: "/access",
      title: "アクセス回線",
      icon: { element: "v-icon", attributes: { icon: "$mdiLan" } },
    },
    {
      header: "仮想ネットワーク設定",
      hiddenOnCollapse: true,
    },
    {
      href: "/vnetwork",
      title: "仮想ネットワーク",
      icon: { element: "v-icon", attributes: { icon: "$mdiLayersTriple" } },
    },
    {
      href: "/vnetwork/site",
      title: "サイト",
      icon: { element: "v-icon", attributes: { icon: "$mdiStickerOutline" } },
      hidden: isViewDelegate,
    },
    {
      href: "/vnetwork/connect",
      title: "VNコネクト",
      icon: { element: "v-icon", attributes: { icon: "$mdiSitemapOutline" } },
      hidden: isViewDelegate,
    },
    {
      href: "/vnetwork/qos",
      title: "QoS",
      icon: { element: "v-icon", attributes: { icon: "$mdiTuneVariant" } },
      hidden: isViewDelegate,
    },
    {
      href: "/vnetwork/packet-filtering",
      title: "パケットフィルタリング",
      icon: {
        element: "v-icon",
        attributes: { icon: "$mdiTrafficLightOutline" },
      },
      hidden: isViewDelegate,
    },
    {
      header: "マルチクラウド",
      hiddenOnCollapse: true,
      hidden: !hasMultiCloudGW,
    },
    {
      href: "/multicloud",
      title: "マルチクラウド",
      icon: { element: "v-icon", attributes: { icon: "$mdiClouds" } },
      hidden: !hasMultiCloudGW || isViewDelegate,
    },
    {
      href: "/multicloud/saas",
      title: "SaaS利用",
      icon: { element: "v-icon", attributes: { icon: "$mdiCloudPlusOutline" } },
      hidden: !hasMultiCloudGW,
    },
    {
      href: "/multicloud/microsoft/napt",
      title: "Microsoft Peering向けNAPT",
      icon: {
        element: "v-icon",
        attributes: { icon: "$mdiArrowDecisionOutline" },
      },
      hidden: !hasMultiCloudGW,
    },
    {
      href: "/multicloud/microsoft/nat",
      title: "Microsoft Peering向けNAT",
      icon: {
        element: "v-icon",
        attributes: { icon: "$mdiTransitConnectionHorizontal" },
      },
      hidden: !hasMultiCloudGW,
    },
    {
      href: "/multicloud/qos",
      title: "クラウド向けQoS",
      icon: { element: "v-icon", attributes: { icon: "$mdiTuneVariant" } },
      hidden: !hasMultiCloudGW || isViewDelegate,
    },
    {
      href: "/multicloud/packet-filtering",
      title: "クラウド向けパケットフィルタリング",
      icon: {
        element: "v-icon",
        attributes: { icon: "$mdiTrafficLightOutline" },
      },
      hidden: !hasMultiCloudGW || isViewDelegate,
    },
    {
      header: "SASEゲートウェイ",
      hiddenOnCollapse: true,
      hidden: isViewDelegate,
    },
    {
      href: "/sasegateway",
      title: "SASEゲートウェイ",
      icon: { element: "v-icon", attributes: { icon: "$mdiSecurityNetwork" } },
      hidden: isViewDelegate,
    },
    {
      href: "/sasegateway/packet-filtering",
      title: "SASEゲートウェイ向けパケットフィルタリング",
      icon: {
        element: "v-icon",
        attributes: { icon: "$mdiTrafficLightOutline" },
      },
      hidden: isViewDelegate,
    },
    {
      header: "IPsec回線",
      hiddenOnCollapse: true,
      hidden: !hasIPsec,
    },
    {
      href: "/ipsec/gateway",
      title: "IPsecGW",
      icon: { element: "v-icon", attributes: { icon: "$mdiKeyOutline" } },
      hidden: !hasIPsec,
    },
    {
      href: "/ipsec/line",
      title: "IPsec回線",
      icon: {
        element: "v-icon",
        attributes: { icon: "$mdiShareVariantOutline" },
      },
      hidden: !hasIPsec || isViewDelegate,
    },
    {
      header: "リモートアクセスGW",
      hiddenOnCollapse: true,
      hidden: !hasCpa5Gsa,
    },
    {
      href: "/remote/access",
      title: "CPA 5G SA",
      icon: {
        element: "v-icon",
        attributes: { icon: "$mdiSignal" },
      },
      hidden: !hasCpa5Gsa,
    },
    {
      header: "インターネット/アプライアンス",
      hiddenOnCollapse: true,
      hidden: contractSummary.isLargeScaleContract,
    },
    {
      href: "/network/site/type1",
      title: "サイトゾーン(Type1)",
      icon: {
        element: "v-icon",
        attributes: { icon: "$mdiMapMarkerRadiusOutline" },
      },
      hidden:
        !hasType1orIntranet ||
        contractSummary.isLargeScaleContract ||
        isViewDelegate,
    },
    {
      href: "/network/site/type4",
      title: "サイト(Type4)",
      icon: {
        element: "v-icon",
        attributes: { icon: "$mdiMapMarkerRadius" },
      },
      hidden: contractSummary.isLargeScaleContract || isViewDelegate,
    },
    {
      href: "/network/object",
      title: "アプライアンスオブジェクト",
      icon: {
        element: "v-icon",
        attributes: { icon: "$mdiServerNetwork" },
      },
      hidden: contractSummary.isLargeScaleContract || isViewDelegate,
    },
    {
      href: "/network/contract",
      title: "契約・設定",
      icon: {
        element: "v-icon",
        attributes: { icon: "$mdiShieldKeyOutline" },
      },
      hidden: contractSummary.isLargeScaleContract,
    },
    {
      // spaceを入れないとaタグとしてレンダリングしてクリックできてしまう
      header: " ",
      hiddenOnCollapse: true,
    },
    {
      href: "/extranet",
      title: "エクストラネット接続",
      icon: { element: "v-icon", attributes: { icon: "$mdiVectorRectangle" } },
      hidden: isViewDelegate,
    },
    {
      href: "/vnetwork/l2l3/connect",
      title: "VN L2/L3コネクト",
      icon: {
        element: "v-icon",
        attributes: { icon: "$mdiArrowExpandVertical" },
      },
      hidden: isViewDelegate,
    },
    {
      href: "/hubspoke",
      title: "Hub&Spoke接続",
      icon: { element: "v-icon", attributes: { icon: "$mdiHubOutline" } },
      hidden: isViewDelegate,
    },
  ] as Array<SidebarItem | SidebarComponentItem | SidebarHeaderItem>;
}
</script>
